/**
 * MemoryResonancePanel — right column, below transcript.
 *
 * TRUTH RULE (iteration 16, formalized):
 * Persistent cross-session memory does not exist in Phase 0. Everything
 * this panel shows is derived from the CURRENT session only. The panel
 * is clearly marked PREVIEW; the number is derived from a real, measurable
 * session-coherence proxy (turns whose status !== 'in_progress' /
 * total turns). This is a *legitimate* session-continuity metric — it
 * measures how many turns of the current conversation have resolved
 * cleanly — but it is NOT organizational memory or cross-session recall.
 *
 * The Phase 1 architecture will keep the same surface API and replace
 * this proxy with real memory-quality scores; nothing about the visual
 * needs to change to reflect that transition.
 *
 * Metric explanation (surfaced via the `?` info affordance):
 *   Session resolution % = 100 × (turns where status !== "in_progress")
 *                                / (total turns)
 * Interpretation:
 *   • 100% at 0 turns is displayed as 0% for honesty.
 *   • Rises as each turn completes (backend confirmation received).
 *   • Any in-flight turn temporarily lowers the reading, which reflects
 *     real activity.
 */

import { useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";
import { PanelShell } from "./PanelShell";

function useAnimatedWave() {
    const canvasRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const w = canvas.clientWidth;
        const h = canvas.clientHeight;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        const ctx = canvas.getContext("2d");
        ctx.scale(dpr, dpr);
        let raf;
        const draw = (now) => {
            ctx.clearRect(0, 0, w, h);
            ctx.lineWidth = 1;
            for (let k = 0; k < 3; k++) {
                ctx.beginPath();
                for (let x = 0; x <= w; x += 3) {
                    const t = now * 0.001 + k * 0.7;
                    const y =
                        h / 2 +
                        Math.sin(x * 0.03 + t) * 8 * (1 - k * 0.25) +
                        Math.sin(x * 0.07 - t * 0.8) * 4;
                    if (x === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
                ctx.strokeStyle = `rgba(0,229,255,${0.5 - k * 0.15})`;
                ctx.stroke();
            }
            raf = requestAnimationFrame(draw);
        };
        raf = requestAnimationFrame(draw);
        return () => cancelAnimationFrame(raf);
    }, []);
    return canvasRef;
}

export function MemoryResonancePanel({ turns = [] }) {
    const canvasRef = useAnimatedWave();
    const [infoOpen, setInfoOpen] = useState(false);

    // Real session-coherence proxy — see file header for definition.
    const completed = turns.filter((t) => t.status !== "in_progress").length;
    const total = turns.length;
    const pct = total === 0 ? 0 : Math.round((completed / total) * 100);

    return (
        <PanelShell
            title="Memory Resonance"
            testId="panel-memory-resonance"
            right={
                <div className="flex items-center gap-1.5">
                    <button
                        type="button"
                        data-testid="memory-info-btn"
                        aria-label="What does this metric mean?"
                        aria-expanded={infoOpen}
                        onClick={() => setInfoOpen((v) => !v)}
                        className="flex h-4 w-4 items-center justify-center rounded-full text-qoryn-textMuted hover:text-qoryn-cyan hover:bg-white/[0.04] transition-colors qoryn-focus"
                        title="Metric definition"
                    >
                        <Info className="h-3 w-3" strokeWidth={2} />
                    </button>
                    <span
                        className="font-mono-ui text-[8px] tracking-widest px-1.5 py-0.5 rounded border border-qoryn-cyan/30 text-qoryn-cyan"
                        data-testid="memory-preview-badge"
                    >
                        PREVIEW
                    </span>
                </div>
            }
        >
            <div className="flex items-baseline gap-2">
                <span
                    className="font-display text-[36px] leading-none font-light tracking-tight text-qoryn-text tabular-nums"
                    data-testid="memory-resonance-value"
                >
                    {pct}%
                </span>
                <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                    session
                </span>
            </div>
            <div className="mt-2 h-14 w-full">
                <canvas
                    ref={canvasRef}
                    style={{ width: "100%", height: "100%", display: "block" }}
                    aria-hidden="true"
                />
            </div>
            <p className="mt-1 font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                Persistent memory arrives in Phase 1
            </p>
            {infoOpen && (
                <div
                    data-testid="memory-info-popover"
                    className="mt-3 pt-3 border-t border-qoryn-border/50 space-y-2"
                    role="region"
                    aria-label="Metric definition"
                >
                    <p className="font-sans text-[11px] leading-relaxed text-qoryn-textDim">
                        <span className="text-qoryn-text">Session resolution %</span> —
                        turns of the current conversation that have completed
                        end-to-end, divided by total turns. Not a memory score,
                        not cross-session recall. Rises as Qoryn finishes each
                        response; dips briefly during in-flight turns.
                    </p>
                    <p className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                        Phase 1 replaces this with true cross-session memory quality.
                    </p>
                </div>
            )}
        </PanelShell>
    );
}

export default MemoryResonancePanel;
